import {
  AfterViewInit,
  Component,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { filter, switchMap, tap } from 'rxjs';
import { User } from 'src/app/models/user';
import { GumUsersService } from 'src/app/services/gumservices/gum-users.service';
import { DialogDeleteConfirmationComponent } from '../dialogs/dialog-delete/dialog-delete-confirmation.component';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterModule } from '@angular/router';
import { UserStateService } from 'src/app/services/signals/user-state.service';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatTable,
    MatPaginatorModule,
    MatSortModule,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    ReactiveFormsModule,
    NgxSpinnerModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatButtonModule,
    RouterModule,
  ],
})
export class UsersComponent implements OnInit, AfterViewInit {
  private spinnerService = inject(NgxSpinnerService);
  displayedColumns = [
    'display',
    'account',
    'displayParentOrganization',
    'createdAt',
    'lastConnection',
    'actions',
  ];

  dataSource!: MatTableDataSource<User>;
  filtersForm: UntypedFormGroup;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  currentUser: User | null = null;
  private userState = inject(UserStateService);

  constructor(
    private userService: GumUsersService,
    private toastr: ToastrService,
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog
  ) {
    this.filtersForm = new UntypedFormGroup({});
  }

  ngOnInit(): void {
    this.currentUser = this.userState.getCurrentUserSignal()();

    this.initFiltersForm();

    const navState = this.getNavigationState();
    if (navState?.createdUserObject) {
      // Si les données sont déjà là, on initialise tout de suite le tableau
      this.initDataSourceWithUser(navState.createdUserObject);
    } else {
      this.loadDatas(); // sinon on charge tout depuis l'API
    }
  }

  isCurrentUser(element: User): boolean {
    return element.id === this.currentUser?.id;
  }

  applyFilters(): void {
    const {
      display,
      account,
      displayParentOrganization,
      createdAt,
      lastConnection,
    } = this.filtersForm.value;

    const filterValue = `${display || ''}¤${account || ''}¤${
      displayParentOrganization || ''
    }¤${createdAt || ''}¤${lastConnection || ''}`;

    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  initFiltersForm(): void {
    this.filtersForm = this.formBuilder.group({
      display: [''],
      account: [''],
      displayParentOrganization: [''],
      createdAt: [''],
      lastConnection: [''],
    });
  }

  loadDatas(): void {
    this.spinnerService.show('spinner');

    this.userService
      .getUsersWithDisplayParentOrganization()
      .subscribe((users) => {
        const filteredUsers = users.filter((u) => !this.isAdminUser(u));

        this.dataSource = new MatTableDataSource(filteredUsers);
        this.dataSource.sort = this.sort;

        this.dataSource.filterPredicate = (data: User, filter: string) => {
          const [displayF, accountF, orgF, createdF, lastConnF] =
            filter.split('¤');

          const matchFilter = [
            data.display?.toLowerCase().includes(displayF.trim().toLowerCase()),
            data.account?.toLowerCase().includes(accountF.trim().toLowerCase()),
            data.displayParentOrganization
              ?.toLowerCase()
              .includes(orgF.trim().toLowerCase()),
            createdF
              ? !!data.createdAt &&
                new Date(data.createdAt as unknown as string)
                  .toLocaleDateString()
                  .includes(new Date(createdF).toLocaleDateString())
              : true,
            lastConnF
              ? !!data.lastConnection &&
                new Date(data.lastConnection as unknown as string)
                  .toLocaleDateString()
                  .includes(new Date(lastConnF).toLocaleDateString())
              : true,
          ];

          return matchFilter.every(Boolean);
        };

        this.applyFilters();
        this.spinnerService.hide('spinner');
      });
  }

  ngAfterViewInit() {
    // attendre que dataSource soit chargé
    const interval = setInterval(() => {
      if (this.dataSource) {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        clearInterval(interval);
      }
    }, 50);
  }

  onDeleteClick(userName: string, userId: string): void {
    const dialogRef = this.dialog.open(DialogDeleteConfirmationComponent, {
      panelClass: 'default-dialog',
      disableClose: true,
    });

    dialogRef.componentInstance.confirmMessage = `Êtes-vous sûr de vouloir supprimer l'utilisateur "${userName}" ?`;

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => result === true),
        tap(() => this.spinnerService.show('spinner')),
        switchMap(() => this.userService.deleteUser(userId))
      )
      .subscribe({
        next: () => {
          this.toastr.success(`Utilisateur supprimé : ${userName}`);
          this.loadDatas();
        },
        error: () => {
          this.toastr.error(
            `Erreur lors de la suppression de l'utilisateur ${userId}`
          );
          this.spinnerService.hide('spinner');
        },
      });
  }

  resetColumnFilter(formControlName: string): void {
    this.filtersForm.controls[formControlName].reset();
    this.applyFilters();
  }

  resetAllColumnFilter(): void {
    this.filtersForm.reset();
    this.applyFilters();
  }

  private getNavigationState(): any {
    return window.history.state;
  }

  private initDataSourceWithUser(newUser: User): void {
    this.spinnerService.show('spinner');

    // Recharge le user depuis GUM pour avoir toutes ses données à jour
    this.userService
      .getUserWithDisplayParentOrganization(newUser.id)
      .pipe(
        switchMap((freshUser) =>
          this.userService.getUsersWithDisplayParentOrganization().pipe(
            tap((users) => {
              // 1. Filtrer les administrateurs
              const filteredUsers = users.filter((u) => !this.isAdminUser(u));

              // 2. N’injecter le user que s’il n’est PAS admin
              const exists = filteredUsers.some((u) => u.id === freshUser.id);

              if (!exists && !this.isAdminUser(freshUser)) {
                filteredUsers.unshift(freshUser);
              }

              // 3. Initialisation de la datasource
              this.dataSource = new MatTableDataSource(filteredUsers);
              this.dataSource.sort = this.sort;

              setTimeout(() => {
                this.dataSource.paginator = this.paginator;
              });

              this.dataSource.filterPredicate = this.buildFilterPredicate();
              this.applyFilters();
              this.spinnerService.hide('spinner');
            })
          )
        )
      )
      .subscribe();
  }

  private isAdminUser(user: User): boolean {
    return (
      user.properties?.some(
        (p) =>
          p.name?.trim() === 'NexusProfil' &&
          p.value?.trim() === 'NexusAdministrateur'
      ) ?? false
    );
  }

  private buildFilterPredicate(): (data: User, filter: string) => boolean {
    return (data: User, filter: string) => {
      const [displayF, accountF, orgF, createdF, lastConnF] = filter.split('¤');
      const matchFilter = [
        data.display?.toLowerCase().includes(displayF.trim().toLowerCase()),
        data.account?.toLowerCase().includes(accountF.trim().toLowerCase()),
        data.displayParentOrganization
          ?.toLowerCase()
          .includes(orgF.trim().toLowerCase()),
        createdF
          ? !!data.createdAt &&
            new Date(data.createdAt as unknown as string)
              .toLocaleDateString()
              .includes(new Date(createdF).toLocaleDateString())
          : true,
        lastConnF
          ? !!data.lastConnection &&
            new Date(data.lastConnection as unknown as string)
              .toLocaleDateString()
              .includes(new Date(lastConnF).toLocaleDateString())
          : true,
      ];
      return matchFilter.every(Boolean);
    };
  }
}
